/**
 * MetaOrganic — orgânico de uma rede da Meta (Instagram ou Página do Facebook).
 *
 * Lê /api/meta-ads/organic/{summary,posts,followers,comments} (coletado pelo backend sob
 * demanda, botão "Atualizar") filtrando pela rede da página, cliente ativo e período global. Mesmo visual
 * do dashboard de Meta Ads: KPIs, gráfico de seguidores, posts e comentários.
 */

import { useMemo, useState, useRef } from 'react'
import './MetaOrganic.css'
import InstagramHistory from './InstagramHistory.jsx'
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts'
import { DataTable } from '../../components/DataTable.jsx'
import KpiCard from '../../components/KpiCard.jsx'
import PageState from '../../components/PageState.jsx'
import { fmtNum } from '../../lib/format.js'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  getMetaOrganicSummary, getMetaOrganicPosts, getMetaOrganicFollowers,
  getMetaOrganicComments, getMetaOrganicAccounts, getMetaOrganicProfile, syncMetaOrganic,
} from '../../db/api.js'
import { useApiData } from '../../hooks/useApiData.js'
import { safeHttpUrl } from '../../lib/safeUrl.js'
import {
  PLATFORM_LABELS, engagementRate, formatRate, summaryKpis,
  followersByDay, followersGrowth, syncMessage,
} from '../../lib/metaOrganic.js'

const BREADCRUMB = (platform) => [{ label: 'Marketing' }, { label: PLATFORM_LABELS[platform] }]
const num = fmtNum
// Mesma altura do gráfico diário de Meta Ads (DailyTrendChart).
const CHART_HEIGHT = 260
const CHART_COLOR = '#4f46e5'
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—')
const clip = (text, max = 90) => {
  const s = String(text || '').replace(/\s+/g, ' ').trim()
  return s.length > max ? `${s.slice(0, max)}…` : s || '—'
}

// Link do post: o permalink vem da Meta, então só http(s) chega ao href.
function PostLink({ permalink, text, max, title }) {
  const url = safeHttpUrl(permalink)
  return url
    ? <a href={url} target="_blank" rel="noopener noreferrer" title={title}>{clip(text, max)}</a>
    : <span title={title}>{clip(text, max)}</span>
}

const POST_COLUMNS = [
  {
    key: 'caption', label: 'Post', sortable: false,
    render: (v, row) => <PostLink permalink={row.permalink} text={v} title={v} />,
  },
  { key: 'platform', label: 'Rede', render: (v) => PLATFORM_LABELS[v] || v },
  { key: 'posted_at', label: 'Data', render: (v) => day(v) },
  { key: 'reach', label: 'Alcance', align: 'right', render: (v) => num(v) },
  { key: 'likes', label: 'Curtidas', align: 'right', render: (v) => num(v) },
  { key: 'comments', label: 'Coment.', align: 'right', render: (v) => num(v) },
  { key: 'saves', label: 'Salvos', align: 'right', render: (v) => num(v) },
  { key: 'rate', label: 'Engaj.', align: 'right', render: (v) => formatRate(v) },
]

function Panel({ icon, title, children, empty }) {
  return (
    <div className="bw-panel">
      <div className="bw-panel-head"><Icon n={icon} s={14} /><span>{title}</span></div>
      <div className="bw-panel-body">{empty ? <div className="bw-panel-empty">{empty}</div> : children}</div>
    </div>
  )
}

export default function MetaOrganic({ platform }) {
  const { activeClient } = useApp()
  return <OrganicDashboard key={`${platform}:${activeClient?.id || ''}`} platform={platform} />
}

function OrganicDashboard({ platform }) {
  const { period, activeClient } = useApp()
  const { start, end } = period || {}
  const [syncing, setSyncing] = useState(false)
  const [notice, setNotice] = useState(null)

  // Cache belongs to this client/platform and expires after five minutes.
  const reads = useRef(new Map())
  function cachedRead(key, fetcher) {
    const entry = reads.current.get(key)
    if (entry && entry.expires > Date.now()) return entry.promise
    const promise = Promise.resolve().then(fetcher)
    reads.current.set(key, { promise, expires: Date.now() + 5 * 60 * 1000 })
    promise.catch(() => {
      if (reads.current.get(key)?.promise === promise) reads.current.delete(key)
    })
    return promise
  }

  const isInstagram = platform === 'instagram'
  const accounts = useApiData(() => getMetaOrganicAccounts(platform), [platform], { initialData: [], skip: !isInstagram || !activeClient })
  const [selectedPage, setSelectedPage] = useState('')
  const pageId = isInstagram ? (accounts.data.find((a) => a.page_id === selectedPage)?.page_id || accounts.data[0]?.page_id) : undefined
  const skip = isInstagram && !pageId
  const profileTotals = useApiData(() => cachedRead(`profile:${pageId}`, async () => ({ pageId, ...await getMetaOrganicProfile(pageId) })), [pageId], { initialData: {}, skip: !isInstagram || !pageId })
  const profileDetails = profileTotals.data.pageId === pageId && !profileTotals.loading && !profileTotals.error
    ? profileTotals.data : {}
  const mediaCount = profileDetails.media_count
  const [failedPhoto, setFailedPhoto] = useState(null)
  const profilePhoto = safeHttpUrl(profileDetails.profile_picture_url)
  const profileWebsite = safeHttpUrl(profileDetails.website)
  const deps = [platform, start, end, pageId]
  const summary = useApiData(() => cachedRead(JSON.stringify(['Summary', pageId, start, end]), () => getMetaOrganicSummary(platform, start, end, pageId)), deps, { initialData: {}, skip })
  const posts = useApiData(() => cachedRead(JSON.stringify(['Posts', pageId, start, end]), () => getMetaOrganicPosts(platform, start, end, pageId)), deps, { initialData: [], skip })
  const followers = useApiData(() => cachedRead(JSON.stringify(['Followers', pageId, start, end]), () => getMetaOrganicFollowers(platform, start, end, pageId)), deps, { initialData: [], skip })
  const comments = useApiData(() => cachedRead(JSON.stringify(['Comments', pageId, start, end]), () => getMetaOrganicComments(platform, start, end, pageId)), deps, { initialData: [], skip })

  const profilePending = isInstagram && pageId && (profileTotals.loading || (!profileTotals.error && profileTotals.data.pageId !== pageId))
  const loading = profilePending || accounts.loading || summary.loading || posts.loading || followers.loading || comments.loading
  const error = accounts.error || summary.error || posts.error || followers.error || comments.error
  const series = useMemo(() => followersByDay(followers.data), [followers.data])
  const growth = followersGrowth(series)
  const profile = accounts.data.find((account) => account.page_id === pageId)

  function reloadAll() {
    reads.current.clear()
    if (isInstagram) accounts.reload()
    if (isInstagram && pageId) profileTotals.reload()
    summary.reload(); posts.reload(); followers.reload(); comments.reload()
  }

  async function refresh() {
    setSyncing(true)
    setNotice(null)
    try {
      setNotice(syncMessage(await syncMetaOrganic()))
      reads.current.clear()
      if (isInstagram) accounts.reload()
      if (isInstagram && pageId) profileTotals.reload()
      summary.reload(); posts.reload(); followers.reload(); comments.reload()
    } catch (err) {
      setNotice({ tone: 'error', text: err?.message || 'Não foi possível atualizar agora.' })
    } finally {
      setSyncing(false)
    }
  }

  const refreshButton = (
    <div className="organic-topbar-actions">
      {/* Perfil é escolha única (não filtro multi como AccountSelect), então fica <select>. */}
      {isInstagram && (
        <select aria-label="Perfil do Instagram" value={pageId || ''}
          onChange={(event) => setSelectedPage(event.target.value)}
          disabled={accounts.loading || !accounts.data.length}
          className="organic-profile-select">
          {!accounts.data.length && <option value="">{accounts.loading ? 'Carregando perfis…' : 'Nenhum perfil conectado'}</option>}
          {accounts.data.map((account) => <option key={account.page_id} value={account.page_id}>{account.name}</option>)}
        </select>
      )}
      <button type="button" className="btn btn-black btn-sm" onClick={refresh} disabled={syncing || !activeClient}>
        <Icon n="refresh" s={12} /> {syncing ? 'Atualizando…' : 'Atualizar'}
      </button>
    </div>
  )

  const empty = !loading && !error && posts.data.length === 0 && series.length === 0

  return (
    <Layout title={PLATFORM_LABELS[platform]} breadcrumb={BREADCRUMB(platform)} showPeriod topbarRight={refreshButton}>
      <div className="dash-shell">
        {!activeClient && <p className="bw-panel-empty">Selecione um cliente para ver o orgânico.</p>}
        {notice && <div className={notice.tone === 'error' ? 'dash-error' : 'bw-panel-empty'} role="status">{notice.text}</div>}

        <PageState loading={loading} error={error && String(error)} onRetry={reloadAll}>
          <>
            {isInstagram ? (
              <section className="organic-profile" aria-label="Resumo do perfil do Instagram">
                <div className="organic-profile-identity">
                  <div className="organic-profile-avatar" aria-hidden="true">
                    {profilePhoto && failedPhoto !== profilePhoto
                      ? <img src={profilePhoto} alt="" referrerPolicy="no-referrer" onError={() => setFailedPhoto(profilePhoto)} />
                      : <span>{(profile?.name || 'I').replace(/^@/, '').charAt(0).toUpperCase()}</span>}
                  </div>
                  <div className="organic-profile-name">
                    <h2>{profile?.name?.replace(/^@/, '') || 'Nenhum perfil conectado'}</h2>
                    {profileDetails.name && <p className="organic-profile-account">{profileDetails.name}</p>}
                    <dl className="organic-profile-stats">
                      <div title={mediaCount == null ? 'Total indisponível. Clique em Atualizar para tentar novamente.' : 'Total de publicações informado pelo Instagram, independente do período'}><dt>posts</dt><dd aria-live="polite">{profileTotals.loading ? '…' : mediaCount == null ? '—' : num(mediaCount)}</dd></div>
                      <div><dt>seguidores</dt><dd>{num(summary.data.followers)}</dd></div>
                      <div><dt>seguindo</dt><dd>{profileDetails.follows_count == null ? '—' : num(profileDetails.follows_count)}</dd></div>
                    </dl>
                    {profileDetails.biography && <p className="organic-profile-bio">{profileDetails.biography}</p>}
                    {profileWebsite && <a className="organic-profile-website" href={profileWebsite} target="_blank" rel="noopener noreferrer">{profileWebsite.replace(/^https?:\/\//i, '').replace(/\/$/, '')}</a>}
                    <dl className="organic-profile-performance">
                      <div title="Alcance somado dos posts do período"><dt>Alcance no período</dt><dd>{num(summary.data.reach)}</dd></div>
                      <div title="Curtidas, comentários, compartilhamentos e salvamentos"><dt>Interações no período</dt><dd>{num(summaryKpis(summary.data).find((k) => k.label === 'Interações')?.value)}</dd></div>
                    </dl>
                  </div>
                </div>
              </section>
            ) : (
              <div className="kpi-grid kpi-grid-4">
                {summaryKpis(summary.data).map((k) => <KpiCard key={k.label} {...k} value={fmtNum(k.value)} />)}
              </div>
            )}

            {isInstagram && <InstagramHistory key={`${pageId}:${start}:${end}`} pageId={pageId} start={start} end={end} cachedRead={cachedRead} />}

            {empty && !isInstagram && (
              <p className="bw-panel-empty">
                Sem dados {platform === 'instagram' ? 'do Instagram' : 'do Facebook'} ainda. Conecte uma Página{platform === 'instagram' ? ' com o Instagram Business vinculado' : ''} e clique em Atualizar.
              </p>
            )}

            <Panel icon="users" title={`Total de seguidores · registros salvos${growth === null ? '' : ` (${growth >= 0 ? '+' : ''}${num(growth)} no período)`}`}
              empty={series.length === 0 ? 'Sem histórico de seguidores. Cada atualização registra o total do dia.' : null}>
              <div className="organic-chart">
                <ResponsiveContainer>
                  <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.4} />
                    <XAxis dataKey="date" tickFormatter={day} tick={{ fontSize: 11 }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={52} domain={['auto', 'auto']} />
                    <Tooltip labelFormatter={day} formatter={(v) => [num(v), 'Seguidores']} labelStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="followers" stroke={CHART_COLOR} strokeWidth={2} fill={CHART_COLOR} fillOpacity={0.15} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel icon="chart" title="Posts" empty={posts.data.length === 0 ? 'Sem posts no período.' : null}>
              <DataTable
                columns={POST_COLUMNS}
                data={posts.data.map((p) => ({ ...p, id: `${p.platform}:${p.post_id}`, rate: engagementRate(p) }))}
                hoverable
                striped
                scrollable
                emptyText="Sem posts no período."
              />
            </Panel>

            <Panel icon="mail" title="Comentários recentes" empty={comments.data.length === 0 ? 'Sem comentários no período.' : null}>
              {comments.data.map((c) => (
                <div key={`${c.platform}:${c.comment_id}`} className="bw-bar-row">
                  <div className="bw-bar-meta">
                    <span className="bw-bar-name">
                      {c.author || 'Anônimo'} · {PLATFORM_LABELS[c.platform] || c.platform}
                    </span>
                    <span className="bw-bar-pct">{day(c.commented_at)}</span>
                  </div>
                  <div><PostLink permalink={c.post_permalink} text={c.body} max={200} /></div>
                </div>
              ))}
            </Panel>
          </>
        </PageState>
      </div>
    </Layout>
  )
}
